import React, { memo } from "react";

import { SkeletonList } from "@hl/base-components/lib/Skeleton";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import {
  Group,
  Stack,
  useMantineTheme,
  Skeleton,
  AppShell,
  Card,
  Box,
  Flex,
  Paper,
  Divider,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { usMintPageApolloStyles } from "~features/MintPage";
import MainCardSkeleton from "~features/MintPage/MintVector/MintCardSkeleton";
import { useSidebarStyles } from "~features/MintPage/apollo/CollectionDetailsSidebar";
import MarketplaceFooterSkeleton from "~features/MintPage/apollo/skeleton/MarketplaceFooterSkeleton";
import { AppHeader } from "~features/layout/Header";

export const GenArtworkSkeleton = () => {
  const theme = useMantineTheme();
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md - 1}px)`);

  const { classes } = usMintPageApolloStyles();

  return (
    <Flex className={classes.mediaSectionContainer} justify="center">
      <Stack spacing="xl" className={classes.mediaSection}>
        {isTablet && (
          <Box mt={32}>
            <Skeleton height={20} width={180} />
          </Box>
        )}
        <Card shadow="sm" p={0} withBorder={false}>
          <Skeleton
            height={isTablet ? 420 : 540}
            width={isTablet ? 320 : 540}
            radius={0}
          />
        </Card>
      </Stack>
    </Flex>
  );
};

const MintPageApolloSkeleton = () => {
  const theme = useMantineTheme();
  const { isMedium, isSmall, isMobile } = useMantineMediaQueries({
    withInitialValues: true,
  });

  const { classes } = usMintPageApolloStyles();
  const { classes: sidebarClasses } = useSidebarStyles({
    isMarketplaceShown: undefined,
  });

  return (
    <AppShell
      header={<AppHeader />}
      styles={{
        main: { paddingTop: "calc(var(--mantine-header-height, 0px))" },
      }}
    >
      <Stack
        align="center"
        sx={{
          borderTop: `1px solid ${theme.colors.divider[0]}`,
          borderBottom: `1px solid ${theme.colors.divider[0]}`,
        }}
        mx={isSmall ? -20 : -16}
        py="xs"
      >
        <Stack
          px={isMedium ? 40 : 20}
          w="100%"
          justify="space-between"
          py={isMobile ? 16 : 32}
          sx={{ overflow: "hidden" }}
          spacing="xs"
        >
          <Flex
            gap="xs"
            direction={isMobile ? "column" : "row"}
            align={isMobile ? "start" : "center"}
          >
            <Skeleton h={80} w={80} />
            <Stack spacing={8}>
              <Skeleton height={29} width={220} />
              <Group spacing="xs">
                <Skeleton width={16} height={16} radius={99} />
                <Skeleton height={14} width={90} />
              </Group>
            </Stack>
          </Flex>
          <Group>
            <Skeleton width="50%" height={14} />
            <Skeleton width={72} height={14} />
          </Group>
          <Stack w="100%" sx={{ overflow: "hidden" }} align="start">
            <MarketplaceFooterSkeleton isScrollable={true} />
            <Group noWrap>
              <SkeletonList
                count={7}
                skeleton={<Skeleton height={14} width={48} />}
              />
            </Group>
          </Stack>
        </Stack>
      </Stack>
      <Group
        spacing={isMedium ? 0 : "xl"}
        align="stretch"
        mx={isMedium ? -20 : 0}
        noWrap={isMedium}
        className={classes.container}
      >
        <GenArtworkSkeleton />
        <Stack
          className={sidebarClasses.sidebarScrollArea + " no-child-table"}
          classNames={{ scrollbar: sidebarClasses.scrollbar }}
          miw={isMedium ? 380 : 300}
        >
          <Stack justify="space-between" h="100%" pb={isSmall ? 32 : 24}>
            <Stack
              className={sidebarClasses.sidebarDetails}
              spacing={isSmall ? 32 : 24}
            >
              <MainCardSkeleton isApollo />
              <Skeleton height={100} width="100%" sx={{ flexShrink: 0 }} />
              <Stack>
                <Group>
                  <SkeletonList
                    count={4}
                    skeleton={
                      <Skeleton height={22} width={50} sx={{ flexShrink: 0 }} />
                    }
                  />
                </Group>
                <Divider />
                <Skeleton height={22} width={160} sx={{ flexShrink: 0 }} />
                <Divider />
                <Skeleton height={22} width={125} sx={{ flexShrink: 0 }} />
                <Divider />
                <Skeleton height={22} width={150} sx={{ flexShrink: 0 }} />
                <Divider />
                <Skeleton height={22} width={150} sx={{ flexShrink: 0 }} />
              </Stack>
              <Paper
                shadow="md"
                radius="md"
                p="md"
                w="100%"
                display="flex"
                sx={{ gap: 20, flexDirection: "column" }}
              >
                <Skeleton height={22} width={150} sx={{ flexShrink: 0 }} />
                <Skeleton width="100%" height={52} sx={{ flexShrink: 0 }} />
                <Skeleton width="100%" height={52} sx={{ flexShrink: 0 }} />
              </Paper>
            </Stack>
          </Stack>
        </Stack>
      </Group>
    </AppShell>
  );
};

export default memo(MintPageApolloSkeleton);
